import {
  InputSelectControl,
  SettingsFormControl,
  SettingsGroup,
  Switch,
  TextInputField
} from '@sequencehq/core-components'
import { MerchantBankAccountDetails } from '../bankDetails.types'

import { BASE_INPUT_FIELD_STYLES } from 'SettingsV2/settings.constants'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import {
  CountriesAlpha2,
  countriesAlpha2,
  State,
  usStates
} from '@sequencehq/api'

export const useCaInternationalAccountForm = ({
  bankDetails,
  showValidationErrors
}: {
  bankDetails?: MerchantBankAccountDetails['value']
  showValidationErrors: boolean
}) => {
  return useForm({
    value: {
      legalName: bankDetails?.canadianInternationalDestination?.legalName ?? '',
      accountNumber:
        bankDetails?.canadianInternationalDestination?.accountNumber ?? '',
      transitNumber:
        bankDetails?.canadianInternationalDestination?.transitNumber ?? '',
      institutionNumber:
        bankDetails?.canadianInternationalDestination?.institutionNumber ?? '',
      bankName: bankDetails?.canadianInternationalDestination?.bankName ?? '',
      swiftCode: bankDetails?.canadianInternationalDestination?.swiftCode ?? '',
      country:
        bankDetails?.canadianInternationalDestination?.bankAddress.country,
      line1:
        bankDetails?.canadianInternationalDestination?.bankAddress.line1 ?? '',
      line2: bankDetails?.canadianInternationalDestination?.bankAddress.line2,
      postcode:
        bankDetails?.canadianInternationalDestination?.bankAddress.postcode ??
        '',
      state: bankDetails?.canadianInternationalDestination?.bankAddress.state,
      town:
        bankDetails?.canadianInternationalDestination?.bankAddress.town ?? '',
      intermediaryBank: Boolean(
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.bankName
      ),
      intermediaryBankName:
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.bankName ?? '',
      intermediaryBankRoutingNumber:
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.routingNumber ?? '',
      intermediaryBankSwiftCode:
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.swiftCode ?? '',
      intermediaryCountry:
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.bankAddress.country,
      intermediaryLine1:
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.bankAddress.line1 ?? '',
      intermediaryLine2:
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.bankAddress.line2,
      intermediaryPostcode:
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.bankAddress.postcode ?? '',
      intermediaryState:
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.bankAddress.state,
      intermediaryTown:
        bankDetails?.canadianInternationalDestination?.intermediaryBank
          ?.bankAddress.town ?? ''
    },
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'legalName',
        validation: [required]
      },
      {
        property: 'accountNumber',
        validation: [required]
      },
      {
        property: 'transitNumber',
        validation: [required]
      },
      {
        property: 'institutionNumber',
        validation: [required]
      },
      {
        property: 'bankName',
        validation: [required]
      },
      {
        property: 'swiftCode',
        validation: [required]
      },
      {
        property: 'country',
        validation: [required]
      },
      {
        property: 'line1',
        validation: [required]
      },
      {
        property: 'line2',
        validation: []
      },
      {
        property: 'postcode',
        validation: [required]
      },
      {
        property: 'state',
        validation: []
      },
      {
        property: 'town',
        validation: [required]
      },
      {
        property: 'intermediaryBank',
        validation: []
      },
      {
        property: 'intermediaryBankName',
        validation: []
      },
      {
        property: 'intermediaryBankRoutingNumber',
        validation: []
      },
      {
        property: 'intermediaryBankSwiftCode',
        validation: []
      },
      {
        property: 'intermediaryCountry',
        validation: []
      },
      {
        property: 'intermediaryLine1',
        validation: []
      },
      {
        property: 'intermediaryLine2',
        validation: []
      },
      {
        property: 'intermediaryPostcode',
        validation: []
      },
      {
        property: 'intermediaryState',
        validation: []
      },
      {
        property: 'intermediaryTown',
        validation: []
      }
    ]
  })
}

export const CaInternationalAccountForm = ({
  fields
}: {
  fields: ReturnType<typeof useCaInternationalAccountForm>['fields']
}) => {
  const resetBankAddress = () => {
    fields.line1.onChange('')
    fields.line2.onChange('')
    fields.postcode.onChange('')
    fields.state.onChange(undefined)
    fields.town.onChange('')
  }

  const resetIntermediaryBankAddress = () => {
    fields.intermediaryLine1.onChange('')
    fields.intermediaryLine2.onChange('')
    fields.intermediaryPostcode.onChange('')
    fields.intermediaryState.onChange(undefined)
    fields.intermediaryTown.onChange('')
  }
  return (
    <>
      <SettingsGroup title="Account details">
        <SettingsFormControl label="Legal name" htmlFor="legal-name">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="John Doe"
            id="legal-name"
            value={fields.legalName.value}
            validationErrors={fields.legalName.validationErrors}
            onChange={e => {
              fields.legalName.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl label="Account number" htmlFor="account-number">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="12345678"
            id="account-number"
            value={fields.accountNumber.value}
            validationErrors={fields.accountNumber.validationErrors}
            onChange={e => {
              fields.accountNumber.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl label="Transit number" htmlFor="transit-number">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder=""
            id="transit-number"
            value={fields.transitNumber.value}
            validationErrors={fields.transitNumber.validationErrors}
            onChange={e => {
              fields.transitNumber.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl
          label="Institution number"
          htmlFor="institution-number"
        >
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="123456789"
            id="institution-number"
            value={fields.institutionNumber.value}
            validationErrors={fields.institutionNumber.validationErrors}
            onChange={e => {
              fields.institutionNumber.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl label="Bank name" htmlFor="bank-name">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder=""
            id="bank-name"
            value={fields.bankName.value}
            validationErrors={fields.bankName.validationErrors}
            onChange={e => {
              fields.bankName.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl label="SWIFT code" htmlFor="swift-code">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder=""
            id="swift-code"
            value={fields.swiftCode.value}
            validationErrors={fields.swiftCode.validationErrors}
            onChange={e => {
              fields.swiftCode.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>
      </SettingsGroup>
      <SettingsGroup title="Bank address">
        <SettingsFormControl label="Country" htmlFor="country">
          <InputSelectControl
            id="country"
            placeholder="Choose a country"
            triggerStyle={{
              width: BASE_INPUT_FIELD_STYLES.width
            }}
            options={Object.entries(countriesAlpha2).map(([key, label]) => ({
              value: key,
              label: label
            }))}
            initialValue={fields.country.value}
            onChange={value => {
              fields.country.onChange(value as CountriesAlpha2)
              resetBankAddress()
            }}
          />
        </SettingsFormControl>

        {fields.country.value && (
          <>
            <SettingsFormControl
              label="Address Line 1"
              htmlFor="address-line-1"
            >
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="123 Main Street"
                id="address-line-1"
                value={fields.line1.value}
                validationErrors={fields.line1.validationErrors}
                onChange={e => {
                  fields.line1.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>

            <SettingsFormControl
              label="Address Line 2 (Optional)"
              htmlFor="address-line-2"
            >
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="Apt, Suite, Unit, Building (optional)"
                id="address-line-2"
                value={fields.line2.value}
                validationErrors={fields.line2.validationErrors}
                onChange={e => {
                  fields.line2.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>

            <SettingsFormControl label="City" htmlFor="city">
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="New York"
                id="city"
                value={fields.town.value}
                validationErrors={fields.town.validationErrors}
                onChange={e => {
                  fields.town.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>

            {fields.country.value === 'US' && (
              <SettingsFormControl label="State" htmlFor="state">
                <InputSelectControl
                  id="state"
                  options={Object.entries(usStates).map(([key, label]) => ({
                    value: key,
                    label: label
                  }))}
                  placeholder="Choose a state"
                  triggerStyle={{
                    width: BASE_INPUT_FIELD_STYLES.width
                  }}
                  initialValue={fields.state.value}
                  onChange={value => {
                    fields.state.onChange(value as State)
                  }}
                />
              </SettingsFormControl>
            )}

            <SettingsFormControl label="ZIP" htmlFor="postcode">
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="10001"
                id="postcode"
                value={fields.postcode.value}
                validationErrors={fields.postcode.validationErrors}
                onChange={e => {
                  fields.postcode.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>
          </>
        )}
      </SettingsGroup>
      <SettingsGroup title="Intermediary bank details">
        <SettingsFormControl
          label="Include intermediary bank details "
          htmlFor="intermediary-bank"
        >
          <Switch
            size="md"
            id="intermediary-bank"
            as="div"
            isChecked={fields.intermediaryBank.value}
            onChange={value => {
              fields.intermediaryBank.onChange(value)
              if (!value) {
                fields.intermediaryBankName.onChange('')
                fields.intermediaryBankSwiftCode.onChange('')
                fields.intermediaryBankRoutingNumber.onChange('')
                fields.intermediaryCountry.onChange(undefined)
                resetIntermediaryBankAddress()
              }
            }}
          />
        </SettingsFormControl>

        {fields.intermediaryBank.value && (
          <>
            <SettingsFormControl
              label="Bank name"
              htmlFor="intermediary-bank-name"
            >
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="12345678"
                id="intermediary-bank-name"
                value={fields.intermediaryBankName.value}
                validationErrors={fields.intermediaryBankName.validationErrors}
                onChange={e => {
                  fields.intermediaryBankName.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>

            <SettingsFormControl
              label="Routing number"
              htmlFor="intermediary-routing-number"
            >
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="12345678"
                id="intermediary-routing-number"
                value={fields.intermediaryBankRoutingNumber.value}
                validationErrors={
                  fields.intermediaryBankRoutingNumber.validationErrors
                }
                onChange={e => {
                  fields.intermediaryBankRoutingNumber.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>
            <SettingsFormControl
              label="SWIFT code"
              htmlFor="intermediary-swift-code"
            >
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="12345678"
                id="intermediary-swift-code"
                value={fields.intermediaryBankSwiftCode.value}
                validationErrors={
                  fields.intermediaryBankSwiftCode.validationErrors
                }
                onChange={e => {
                  fields.intermediaryBankSwiftCode.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>

            <SettingsFormControl label="Country" htmlFor="intermediary-country">
              <InputSelectControl
                id="intermediary-country"
                placeholder="Choose a country"
                triggerStyle={{
                  width: BASE_INPUT_FIELD_STYLES.width
                }}
                options={Object.entries(countriesAlpha2).map(
                  ([key, label]) => ({
                    value: key,
                    label: label
                  })
                )}
                initialValue={fields.intermediaryCountry.value}
                onChange={value => {
                  fields.intermediaryCountry.onChange(value as CountriesAlpha2)
                  resetIntermediaryBankAddress()
                }}
              />
            </SettingsFormControl>

            {fields.intermediaryCountry.value && (
              <>
                <SettingsFormControl
                  label="Address Line 1"
                  htmlFor="intermediary-address-line-1"
                >
                  <TextInputField
                    {...BASE_INPUT_FIELD_STYLES}
                    placeholder="123 Main Street"
                    id="intermediary-address-line-1"
                    value={fields.intermediaryLine1.value}
                    validationErrors={fields.intermediaryLine1.validationErrors}
                    onChange={e => {
                      fields.intermediaryLine1.onChange(e.target.value)
                    }}
                  />
                </SettingsFormControl>

                <SettingsFormControl
                  label="Address Line 2 (Optional)"
                  htmlFor="intermediary-address-line-2"
                >
                  <TextInputField
                    {...BASE_INPUT_FIELD_STYLES}
                    placeholder="Apt, Suite, Unit, Building (optional)"
                    id="intermediary-address-line-2"
                    value={fields.intermediaryLine2.value}
                    validationErrors={fields.intermediaryLine2.validationErrors}
                    onChange={e => {
                      fields.intermediaryLine2.onChange(e.target.value)
                    }}
                  />
                </SettingsFormControl>

                <SettingsFormControl label="City" htmlFor="intermediary-city">
                  <TextInputField
                    {...BASE_INPUT_FIELD_STYLES}
                    placeholder="New York"
                    id="intermediary-city"
                    value={fields.intermediaryTown.value}
                    validationErrors={fields.intermediaryTown.validationErrors}
                    onChange={e => {
                      fields.intermediaryTown.onChange(e.target.value)
                    }}
                  />
                </SettingsFormControl>

                {fields.intermediaryCountry.value === 'US' && (
                  <SettingsFormControl
                    label="State"
                    htmlFor="intermediary-state"
                  >
                    <InputSelectControl
                      id="intermediary-state"
                      options={Object.entries(usStates).map(([key, label]) => ({
                        value: key,
                        label: label
                      }))}
                      placeholder="Choose a state"
                      triggerStyle={{
                        width: BASE_INPUT_FIELD_STYLES.width
                      }}
                      initialValue={fields.intermediaryState.value}
                      onChange={value => {
                        fields.intermediaryState.onChange(value as State)
                      }}
                    />
                  </SettingsFormControl>
                )}

                <SettingsFormControl
                  label="ZIP"
                  htmlFor="intermediary-postcode"
                >
                  <TextInputField
                    {...BASE_INPUT_FIELD_STYLES}
                    placeholder="10001"
                    id="intermediary-postcode"
                    value={fields.intermediaryPostcode.value}
                    validationErrors={
                      fields.intermediaryPostcode.validationErrors
                    }
                    onChange={e => {
                      fields.intermediaryPostcode.onChange(e.target.value)
                    }}
                  />
                </SettingsFormControl>
              </>
            )}
          </>
        )}
      </SettingsGroup>
    </>
  )
}

export const formatCaInternationalFormData = (
  formData: ReturnType<
    typeof useCaInternationalAccountForm
  >['queries']['formData']
) => {
  const {
    country,
    line1,
    line2,
    state,
    town,
    postcode,
    intermediaryBank,
    intermediaryBankName,
    intermediaryBankRoutingNumber,
    intermediaryBankSwiftCode,
    intermediaryCountry,
    intermediaryLine1,
    intermediaryLine2,
    intermediaryPostcode,
    intermediaryState,
    intermediaryTown,
    ...rest
  } = formData
  return {
    ...rest,
    bankAddress: {
      country: country as CountriesAlpha2,
      line1,
      line2,
      state,
      town,
      postcode
    },
    ...(intermediaryBank && {
      intermediaryBank: {
        bankName: intermediaryBankName,
        routingNumber: intermediaryBankRoutingNumber,
        swiftCode: intermediaryBankSwiftCode,
        bankAddress: {
          country: intermediaryCountry as CountriesAlpha2,
          line1: intermediaryLine1,
          line2: intermediaryLine2,
          state: intermediaryState,
          town: intermediaryTown,
          postcode: intermediaryPostcode
        }
      }
    })
  }
}
